/**
 * Operations dashboard — refunds/returns, subscription health, order cadence
 * (live from orders/refunds/subscriptions), plus operational-efficiency,
 * fulfillment, inventory, and support as locked notice cards. Clients hide what
 * they don't need (Customize on the dashboard / per-card).
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "../lib/api-context.js";
import { usePeriod } from "../lib/period.js";
import { useDrill } from "../lib/drill.js";
import { useDemoMode } from "../lib/demo-mode.js";
import { useSetupGate, SetupLocked, PullingData, SyncError } from "../lib/setup-gate.js";
import { Hideable } from "../lib/hide.js";
import { SortableArea } from "../lib/sortable-cards.js";
import { MetricCard } from "../components/MetricCard.js";
import { Card, CardHead, moneyFmt } from "../components/cards.js";
import { ComboChart } from "../components/ComboChart.js";
import { StackedBarLineChart, type StackedPoint } from "../components/StackedBarLineChart.js";
import { Segmented } from "../components/Segmented.js";
import { Sparkline } from "../components/Sparkline.js";
import type { Capabilities } from "../lib/card-requirements.js";
import { DataTrustCardNotice } from "../lib/data-trust-banner.js";
import { PageLoadError } from "../components/PageLoadError.js";
import { pageLoadState } from "../lib/page-load-state.js";

interface Retention {
  summary: { repeatRate: number | null; ordersPerCustomer: number; avgDaysToSecond: number | null; refundRatePct: number | null; refundedAmount: number; refundedOrders: number; chargebackRatePct: number | null; chargebackAmount: number; chargebackCount: number; unmatchedChargebackCount: number; unmatchedChargebackAmount: number; revenue: number };
  refundTrend: Array<{ month: string; incomplete: boolean; orders: number; refundedOrders: number; refundedAmount: number; revenue: number }>;
}
type BucketKey = "sameDay" | "d1" | "d3" | "d7" | "d14" | "d30" | "beyond";
interface RefundMonthPoint { month: string; count: number; refundedOrders: number; amount: number; countByBucket: Record<BucketKey, number>; amountByBucket: Record<BucketKey, number>; refundRatePct: number | null; salesCount?: number; salesValue?: number }
interface RefundAvgPoint { month: string; refundRatePct: number | null; avgSameDay: number | null; avgWithin7: number | null; avgWithin30: number | null }
interface RefundProductPoint { product: string; amount: number; revenue: number; amountByBucket: Record<BucketKey, number> }
interface RefundAnalysis {
  byPurchase: RefundMonthPoint[];
  byRefund: RefundMonthPoint[];
  avgValue: RefundAvgPoint[];
  byProduct: RefundProductPoint[];
  buckets: Array<{ key: BucketKey; label: string }>;
  /** Flag 8: true when refunds have no true source date yet — the timing/speed
   *  views show a transitional "still importing" state, never fabricated timing. */
  refundDatesImporting: boolean;
  missingSaleBasis?: { orders: number; amount: number };
}
interface RefundEvent {
  id: string;
  provider: string;
  orderId: string;
  saleDate: string;
  refundDate: string;
  refundDateConfirmed: boolean;
  daysAfterSale: number;
  amount: number;
  originalSaleAmount: number;
  missingSaleBasis: boolean;
  reason: string | null;
}

// Refund rate BY COHORT (order-date attribution + maturity marker) — the
// "True refund rate by month sold" card. See services/metrics/refund-analysis.ts.
interface RefundCohortPoint { month: string; orders: number; revenue: number; refundedOrders: number; refundedAmount: number; rateCount: number | null; rateAmount: number | null; settling: boolean }
interface RefundCohortProduct { product: string; orders: number; revenue: number; refundedOrders: number; refundedAmount: number; rateCount: number | null; rateAmount: number | null }
interface RefundCohorts {
  cohorts: RefundCohortPoint[];
  byProduct: RefundCohortProduct[];
  settleDays: number;
  lagBasis: "measured" | "default";
  matureCutoff: string;
  hasSettled: boolean;
}

/** The flag-8 transitional state: shown in place of a timing/speed view when the
 *  account's refunds have no true source date yet (never fabricated ingest-date
 *  timing). Dollar totals and the refund rate don't need the date and stay valid. */
function RefundTimingImporting({ what }: { what: string }): React.ReactElement {
  return (
    <div style={{ marginTop: 12, padding: "14px 16px", border: "1px dashed var(--border)", borderRadius: 8, background: "var(--surface)", color: "var(--body)", fontSize: 12.5, lineHeight: 1.55 }}>
      <b style={{ color: "var(--text)" }}>Refund dates still importing.</b> {what} completes after the first full refund import — your provider hasn't supplied the true refund date yet, and we don't guess it from the ingest date. Your refund totals and refund rate above are already correct; only the sale-to-refund timing waits for real dates.
    </div>
  );
}
/** Subset of the shared /dashboard/profit-model cockpit response that the
 *  operational-efficiency cards read (read-only — Exec owns this endpoint). */
interface Lever { value: number | null; status: string }
interface ProfitModel { operationalDrag: Lever; contributionMargin: Lever; overheadPerCustomer: Lever; monthlyOverhead: number | null; monthly: Array<{ key: string; incomplete: boolean; customers: number; revenue: number; overhead: number }> }

const pct = (n: number | null | undefined): string => (n === null || n === undefined ? "—" : `${(n * 100).toFixed(1)}%`);

/**
 * Relative change (fraction, +0.24 = 24% higher): last FULL month vs the month
 * before. Expects an ascending "YYYY-MM" series (both sources here are). The
 * partial current month is excluded; null (skip the pill) with fewer than 3
 * full months or a zero prior — never a fake or divide-by-zero delta.
 */
function lastFullMonthDelta(series: Array<{ month: string; value: number }>): number | null {
  if (series.length < 3) return null;
  const last = series[series.length - 1];
  const prior = series[series.length - 2];
  if (last === undefined || prior === undefined || prior.value <= 0) return null;
  return (last.value - prior.value) / prior.value;
}

export default function OperationsPage(): React.ReactElement {
  const api = useApiClient();
  const period = usePeriod();
  const drill = useDrill();
  const demo = useDemoMode();
  const gate = useSetupGate();
  const ready = gate.data?.previewAllowed === true && gate.data.syncStatus === "ready";

  const retention = useQuery({ queryKey: ["ops-retention", period.cacheKey], enabled: ready, queryFn: () => api.get<Retention>("/dashboard/retention-overview", { ...period.query }) });
  const capsQ = useQuery({ queryKey: ["capabilities"], enabled: ready, queryFn: () => api.get<Capabilities>("/dashboard/capabilities"), staleTime: 0, refetchOnWindowFocus: true });
  // Operational-efficiency cards read the SAME cockpit numbers Exec uses (read-only)
  // so operational drag matches across both pages; period-scoped to the global filter.
  const profitModel = useQuery({ queryKey: ["ops-profit-model", period.cacheKey], enabled: ready && (demo || capsQ.data?.financialBasis === "profit"), queryFn: () => api.get<ProfitModel>("/dashboard/profit-model", { ...period.query }) });
  // Refund Analysis (Praxis): refunds bucketed by how fast they came back, by sale
  // month + refund month, + avg refund value. Period/product/source-filtered server-side.
  const refundAnalysis = useQuery({ queryKey: ["ops-refund-analysis", period.cacheKey], enabled: ready, queryFn: () => api.get<RefundAnalysis>("/dashboard/refund-analysis", { ...period.query }) });
  // Refund rate BY COHORT (founder 2026-07-13): order-date attribution + per-product
  // settled rate + maturity marker. Feeds the "True refund rate by month sold" card.
  const refundCohorts = useQuery({ queryKey: ["ops-refund-cohorts", period.cacheKey], enabled: ready, queryFn: () => api.get<RefundCohorts>("/dashboard/refund-cohorts", { ...period.query }) });
  const gateLoad = pageLoadState(gate);
  const requiredLoad = pageLoadState(
    capsQ,
    retention,
    profitModel,
    refundAnalysis,
    refundCohorts,
  );

  if (gateLoad.failed) {
    return (
      <PageLoadError
        title="We couldn't load your operations numbers"
        retrying={gateLoad.retrying}
        onRetry={gateLoad.retry}
      />
    );
  }
  if (gate.isLoading) return <p style={{ color: "var(--body)" }}>Loading…</p>;
  if (gate.data?.locked) return <SetupLocked />;
  if (gate.data?.syncStatus === "error") return <SyncError message={gate.data.errorMessage} />;
  if (gate.data?.syncStatus === "pending") return <PullingData />;
  if (requiredLoad.failed) {
    return (
      <PageLoadError
        title="We couldn't load your operations numbers"
        retrying={requiredLoad.retrying}
        onRetry={requiredLoad.retry}
      />
    );
  }

  const caps = capsQ.data;
  const expenseReady = demo || caps?.financialBasis === "profit";
  const rs = retention.data?.summary;
  const refundPctRev = rs && rs.revenue > 0 ? rs.refundedAmount / rs.revenue : null;
  const chargebackPctRev = rs && rs.revenue > 0 ? rs.chargebackAmount / rs.revenue : null;
  const rt = retention.data?.refundTrend ?? [];
  // Avg refund = refunded $ ÷ refunded orders over the EXACT selected window (from
  // the summary). Never average monthly averages: months with one refund and one
  // hundred refunds must not carry equal weight.
  const avgRefund = rs && rs.refundedOrders > 0 ? rs.refundedAmount / rs.refundedOrders : null;
  // Sparks chart the real monthly series (incl. the partial current month, same
  // as the chart below); the pill compares the last FULL month with the prior.
  const rateSeries = rt.map((m) => ({ month: m.month, value: m.orders > 0 ? m.refundedOrders / m.orders : 0 }));
  const rateDelta = lastFullMonthDelta(rateSeries.filter((_, i) => !rt[i]?.incomplete));

  // Operational efficiency (read-only from the cockpit — same numbers as Exec).
  const pm = profitModel.data;
  const opDrag = pm?.operationalDrag.value ?? null; // overhead ÷ contribution margin
  const ohPerCust = pm?.overheadPerCustomer.value ?? null;
  // Overhead % of revenue = the SAME three complete months on both sides. Using
  // today's overhead over three historical revenue months rewrote the past and
  // disagreed with the card's own effective-dated trend whenever overhead changed.
  const monthlyOh = pm?.monthlyOverhead ?? null;
  const completeActiveMonths = (pm?.monthly ?? []).filter((m) => !m.incomplete && m.revenue > 0);
  const latestThreeMonths = completeActiveMonths.slice(-3);
  const latestThreeRevenue = latestThreeMonths.reduce((sum, month) => sum + month.revenue, 0);
  const latestThreeOverhead = latestThreeMonths.reduce((sum, month) => sum + month.overhead, 0);
  const ohPctRev = monthlyOh !== null && latestThreeRevenue > 0 ? latestThreeOverhead / latestThreeRevenue : null;
  // Overhead-per-customer trend (founder 2026-07-07: "date it so you can see the
  // trend go up/down — tracks efficiency over time"). Each point uses the
  // overhead that was actually in effect in that month; applying today's cost
  // backward would rewrite history whenever the owner changed overhead. Excludes
  // the still-filling current month (its low count would spike the last point).
  const ohPerCustSpark = monthlyOh !== null
    ? (pm?.monthly ?? []).filter((m) => !m.incomplete && m.customers > 0).map((m) => m.overhead / m.customers)
    : [];
  // Overhead % of revenue, month by month on a 3-MONTH TRAILING AVERAGE (founder
  // 2026-07-15: "show the previous 12 months on a 3-month trailing average").
  // Sum of each month's actual overhead ÷ sum of revenue over the trailing three
  // active months smooths a lumpy month while preserving the real historical
  // cost. Excludes the still-filling current month.
  const ohPctSpark = monthlyOh !== null
    ? (() => {
        return completeActiveMonths
          .map((_, i) => {
            const win = completeActiveMonths.slice(Math.max(0, i - 2), i + 1); // trailing 3 (or fewer at the very start)
            const revenue = win.reduce((s, m) => s + m.revenue, 0);
            const overhead = win.reduce((s, m) => s + m.overhead, 0);
            return revenue > 0 ? overhead / revenue : 0;
          })
          .slice(-12);
      })()
    : [];

  return (
    <div>
      <div style={kicker}>Operations</div>
      <h1 style={{ margin: "7px 0 8px", fontSize: 27 }}>{expenseReady ? "How efficiently are we running?" : "What is coming back?"}</h1>
      <p style={{ color: "var(--body)", fontSize: 14.5, maxWidth: 820, lineHeight: 1.5, marginTop: 0 }}>
        {expenseReady
          ? "What's coming back as refunds, and how much of your margin overhead eats — live from your data."
          : "Refunds and returns from your real order history."}
      </p>

      <SortableArea pageKey="operations">
      {/* Founder 2026-07-13 consolidation: the 9-card refund section → 3 cards.
          1) headline tile (rate + $ / % rev / avg as sub-stats), 2) True refund
          rate by month sold (cohort, per-product drill), 3) Refund money over time
          (flow by refund date, detail tables in its drill). Nothing lost — every
          old view lives inside these three. */}
      <Hideable id="ops-refunds-returns" label="Refund rate" span={12}>
        <RefundHeadlineCard rs={rs} loading={retention.isLoading} rateSeries={rateSeries.map((p) => p.value)} rateDelta={rateDelta} avgRefund={avgRefund} refundPctRev={refundPctRev} periodLabel={period.label} onClick={() => drill.open({ title: "Refund rate", metric: "refund_rate", dimension: "month" })} />
      </Hideable>
      <Hideable id="ops-refunds-by-sale-date" label="True refund rate by month sold" span={12}>
        <RefundCohortCard data={refundCohorts.data} loading={refundCohorts.isLoading || !ready} />
      </Hideable>
      <Hideable id="ops-refund-trend" label="Refund money over time" span={12}>
        <RefundFlowCard ra={refundAnalysis.data} loading={refundAnalysis.isLoading} />
      </Hideable>

      {caps?.chargebacks === true ? <Hideable id="ops-chargebacks" label="Chargebacks" span={12}>
      <Section title="Chargebacks" sub="Disputes where a customer went to their bank instead of asking you for a refund — the costliest kind of return. Connect a dispute feed to light these up.">
        {<MetricCard title="Chargeback rate" loading={retention.isLoading} value={pct(rs?.chargebackRatePct)} sub={`of orders, ${period.label.toLowerCase()}`} goodIsDown caps={caps} requires={["chargebacks"]} trendMetric="chargeback_rate" onClick={() => drill.open({ title: "Chargeback rate", metric: "chargeback_rate", dimension: "month" })} explain={["<b>What it is:</b> The share of your orders that turned into a chargeback — a customer disputing the charge with their bank. High dispute rates can trigger processor or card-network monitoring.", "<b>How it's calculated:</b> Orders with any dispute ÷ total orders over the selected dates. Won disputes still count because winning the money back does not erase the dispute.", "<b>Example:</b> 60 disputed orders ÷ 15,000 orders = 0.4%.", "<b>Note:</b> Follows the sidebar product filter; click in to slice by month, product, or channel. Both sides use the order date, so this shows which sales later became disputes. Card networks use their own month and payment rules for formal monitoring, so this is not a processor-risk threshold calculator."]} />}
        {<MetricCard title="Chargebacks $" loading={retention.isLoading} value={moneyFmt(rs?.chargebackAmount ?? 0)} sub={period.label.toLowerCase()} goodIsDown caps={caps} requires={["chargebacks"]} trendMetric="chargeback_amount" onClick={() => drill.open({ title: "Chargebacks", metric: "chargeback_amount", dimension: "month" })} explain={["<b>What it is:</b> Confirmed chargeback losses plus the money still at risk in open disputes, before any extra processor fees.", "<b>How it's calculated:</b> Open and lost dispute amounts, converted to your currency at the dispute-open exchange rate, added up. Won disputes are excluded because that money came back.", "<b>Example:</b> $3,900 across 60 open or lost disputed orders.", "<b>Note:</b> Follows the sidebar product filter; click in to slice by month, product, or channel. When split by product, each order's dispute is spread across its items by revenue share, so a $120 chargeback on an $80+$40 cart shows as $80/$40 — never $120 twice."]} />}
        {<MetricCard title="Chargebacks % of revenue" loading={retention.isLoading} value={pct(chargebackPctRev)} sub="cash at risk ÷ revenue" caps={caps} requires={["chargebacks"]} onClick={() => drill.open({ title: "Chargebacks (drives % of revenue)", metric: "chargeback_amount", dimension: "month" })} explain={["<b>What it is:</b> Lost and still-open dispute dollars as a share of revenue. This shows how much cash is lost or at risk, not how often disputes happen.", "<b>How it's calculated:</b> Open and lost dispute dollars divided by sales before refunds and after sales tax is removed, over the same order dates.", "<b>Example:</b> $3,900 divided by $1,157,837 = 0.3%.", "<b>Note:</b> A dispute can include tax or shipping, while dashboard revenue removes sales tax. That makes this a cash-impact read. A product filter keeps orders containing that product and uses the whole order on both sides. Chargeback rate counts orders, so the two numbers will not match."]} />}
        {<MetricCard title="Chargeback count" loading={retention.isLoading} value={(rs?.chargebackCount ?? 0).toLocaleString()} sub="disputed orders" goodIsDown caps={caps} requires={["chargebacks"]} trendMetric="chargeback_count" onClick={() => drill.open({ title: "Chargeback count", metric: "chargeback_count", dimension: "month" })} explain={["<b>What it is:</b> How many orders were disputed over the selected dates — the raw count behind the rate.", "<b>How it's calculated:</b> Distinct orders with any dispute, including disputes you later won.", "<b>Example:</b> 60 disputed orders.", "<b>Note:</b> Follows the sidebar product filter; click in to slice by month, product, or channel. Counts whole orders, bucketed by the order's date (not the date the dispute opened)."]} />}
        {(rs?.unmatchedChargebackCount ?? 0) > 0 && (
          <div style={{ gridColumn: "1 / -1", padding: "10px 12px", border: "1px solid var(--border)", borderRadius: 8, background: "var(--surface-2)", color: "var(--body)", fontSize: 12, lineHeight: 1.5 }}>
            <b style={{ color: "var(--text)" }}>{rs!.unmatchedChargebackCount.toLocaleString()} dispute{rs!.unmatchedChargebackCount === 1 ? " is" : "s are"} not linked to an order yet.</b>{" "}
            They are left out of these order-based cards until matched{rs!.unmatchedChargebackAmount > 0 ? `; ${moneyFmt(rs!.unmatchedChargebackAmount)} is still open or lost` : ""}.
          </div>
        )}
      </Section>
      </Hideable> : (
        <Hideable id="ops-chargebacks-unavailable" label="Chargebacks" span={12}>
          <Card style={{ marginBottom: 0 }}>
            <CardHead title="Chargebacks aren't available from this source" />
            <div role="status" style={{ marginTop: 10, color: "var(--body)", fontSize: 13, lineHeight: 1.5 }}>
              Refunds are included above. This order history does not include chargebacks, so we leave them out instead of showing a false zero.
            </div>
          </Card>
        </Hideable>
      )}

      {expenseReady && <Hideable id="ops-efficiency" label="Operational efficiency" span={12}>
      <Section title="Operational efficiency" sub="How much of your margin overhead and labor eat — lower is better. The locked cards light up once you add the matching cost detail.">
        <MetricCard
          title="Operational drag"
          trustWarning={<DataTrustCardNotice card="Operational drag" />}
          companyWide
          value={opDrag === null ? "—" : pct(opDrag)}
          sub="overhead ÷ contribution margin"
          goodIsDown
          loading={profitModel.isLoading}
          requires={["overhead", "contributionMargin"]}
          caps={caps}
          explain={[
            "<b>What it is:</b> How much of your contribution margin gets eaten by fixed overhead. Lower is better — the cleanest read on whether you scale profitably.",
            "<b>How it's calculated:</b> Overhead for the period ÷ contribution margin for the period.",
            "<b>Example (sample data · year-to-date):</b> $132,000 overhead (6 months × $22k) ÷ $611,997 margin = 0.22× (22%).",
            "<b>Note:</b> Blended, so it cannot be split by product. Each date uses the overhead recorded for that period. If the history is thin, the card warns that earlier dates are still estimates. It needs both overhead and product costs.",
          ]}
        />
        <MetricCard
          title="Overhead % of revenue"
          trustWarning={<DataTrustCardNotice card="Overhead % of revenue" />}
          companyWide
          value={ohPctRev === null ? "—" : pct(ohPctRev)}
          sub="latest, 3-month trailing"
          spark={ohPctSpark}
          goodIsDown
          loading={profitModel.isLoading}
          requires={["overhead", "contributionMargin"]}
          caps={caps}
          explain={[
            "<b>What it is:</b> Overhead as a share of revenue, month by month, so you can watch it fall as you scale. The raw weight of your fixed costs on the top line.",
            "<b>How it's calculated:</b> For each point, total overhead for the trailing three complete months ÷ total revenue from those same three months. That is the same as average monthly overhead ÷ average monthly revenue.",
            "<b>Example (sample data):</b> $101,136 of overhead ÷ $632,098 of revenue across the same three complete months = 16.0%.",
            "<b>Note:</b> Blended, because overhead is not tracked by product. The still-forming current month is left out. Complete accounting months use reviewed actual expenses. Missing months use the approved planning amount and are labeled.",
          ]}
        />
        <MetricCard
          title="Overhead per customer"
          trustWarning={<DataTrustCardNotice card="Overhead per customer" />}
          companyWide
          value={ohPerCust === null ? "—" : moneyFmt(ohPerCust)}
          sub="overhead ÷ customers"
          goodIsDown
          loading={profitModel.isLoading}
          requires={["overhead"]}
          caps={caps}
          spark={ohPerCustSpark.length > 2 ? ohPerCustSpark : undefined}
          explain={[
            "<b>What it is:</b> Your monthly overhead spread across the customers you served. A falling number as you grow means operating leverage is working in your favor.",
            "<b>How it's calculated:</b> Monthly overhead ÷ your average monthly customers, averaged over the active months that match the selected window (30 days ≈ 1 month, 90 days ≈ 3, a year ≈ 12) — so it moves with the date picker.",
            "<b>Example (sample data · year-to-date):</b> $22,000 overhead ÷ 2,311.7 average monthly customers = $9.52.",
            "<b>Note:</b> Blended, because overhead is not tracked by product. The main number uses your approved planning overhead with the average monthly customer count for your chosen window. In the trend, complete accounting months use reviewed actual expenses and missing months use the approved planning amount.",
          ]}
        />
        <LockedCard
          title="Labor % of revenue"
          reason="Add labor detail to your costs to unlock — type it in, upload a P&L, or connect QuickBooks."
          explain={[
            "<b>What it is:</b> Your labor cost as a share of revenue — how much of every sales dollar goes to paying people.",
            "<b>How it's calculated:</b> Labor cost ÷ revenue for the period.",
            "<b>Note:</b> Locked by design. Today overhead is a single number, so labor needs to be split out from it first (type it in, upload a P&L, or connect QuickBooks).",
          ]}
        />
        <LockedCard
          title="Labor % of CM"
          reason="Add labor detail to your costs to unlock — type it in, upload a P&L, or connect QuickBooks."
          explain={[
            "<b>What it is:</b> Labor cost as a share of your contribution margin — the labor-side twin of operational drag.",
            "<b>How it's calculated:</b> Labor cost ÷ contribution margin for the period.",
            "<b>Note:</b> Locked by design. Needs labor split out from your single overhead number first (type it in, upload a P&L, or connect QuickBooks).",
          ]}
        />
        <LockedCard
          title="Operating leverage"
          reason="Builds as we track your overhead over time — shows how much fixed cost creeps in per $1M of new revenue."
          explain={[
            "<b>What it is:</b> How much your fixed costs creep up as revenue grows — measured as the extra fixed cost per $1M of new revenue. Low leverage means you scale without bloating overhead.",
            "<b>How it's calculated:</b> The change in your fixed costs compared against the change in revenue, across two points in time.",
            "<b>Note:</b> Locked by design. It needs a couple of overhead snapshots at different revenue levels, which build up as we track your overhead over time.",
          ]}
        />
      </Section>
      </Hideable>}
      </SortableArea>
    </div>
  );
}

/** A visibly-disabled card — shows the metric + WHY it's locked (founder:
 *  "visibly-disabled-with-reason"), matching MetricCard's locked look. Local to
 *  this page, so no shared-component change. */
function LockedCard({ title, reason, explain }: { title: string; reason: string; explain?: string[] }): React.ReactElement | null {
  // Public demo: a locked "add labor detail / connect QuickBooks" card is just a
  // setup nag — hide it for visitors.
  if (useDemoMode()) return null;
  return (
    <Card style={{ marginBottom: 0, height: "100%" }}>
      <CardHead title={title} explain={explain} />
      <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 7, color: "var(--body-faint)", fontSize: 12.5, lineHeight: 1.45 }}>
        <span style={{ fontSize: 14 }} aria-hidden="true">🔒</span>
        <span>{reason}</span>
      </div>
    </Card>
  );
}

function Section({ title, sub, children }: { title: string; sub?: string; children: React.ReactNode }): React.ReactElement {
  return (
    <div style={{ marginTop: 26 }}>
      <h2 style={{ fontSize: 18, margin: 0 }}>{title}</h2>
      {sub && <p style={{ color: "var(--body)", fontSize: 13, margin: "4px 0 0" }}>{sub}</p>}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 14, marginTop: 12 }}>{children}</div>
    </div>
  );
}

const kicker: React.CSSProperties = { color: "var(--accent)", fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" };

/** Refund detail table (Praxis): month rows with sales, refunds, refund rate, and the
 *  cumulative refunded $ within same-day / ≤7-day / ≤30-day windows of the sale. */
function RefundTable({ rows, byPurchase }: { rows: RefundMonthPoint[]; byPurchase: boolean }): React.ReactElement {
  const w7: BucketKey[] = ["sameDay", "d1", "d3", "d7"];
  const w30: BucketKey[] = ["sameDay", "d1", "d3", "d7", "d14", "d30"];
  const cum = (m: RefundMonthPoint, keys: BucketKey[]): number => keys.reduce((s, k) => s + m.amountByBucket[k], 0);
  const data = [...rows].reverse(); // newest month first
  const thR: React.CSSProperties = { textAlign: "right", padding: "6px 9px", fontWeight: 600, whiteSpace: "nowrap" };
  const tdR: React.CSSProperties = { textAlign: "right", padding: "6px 9px", whiteSpace: "nowrap" };
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
        <thead>
          <tr style={{ color: "var(--body-faint)", borderBottom: "1px solid var(--border)" }}>
            <th style={{ textAlign: "left", padding: "6px 9px", fontWeight: 600 }}>Month</th>
            {byPurchase && <th style={thR}>Sales</th>}
            {byPurchase && <th style={thR}>Sales $</th>}
            <th style={thR}>Refund events</th>
            <th style={thR}>Refunded $</th>
            {byPurchase && <th style={thR}>Refund rate</th>}
            <th style={thR}>Same-day $</th>
            <th style={thR}>≤7-day $</th>
            <th style={thR}>≤30-day $</th>
          </tr>
        </thead>
        <tbody>
          {data.map((m) => (
            <tr key={m.month} style={{ borderBottom: "1px solid var(--border-faint)" }}>
              <td style={{ textAlign: "left", padding: "6px 9px", fontWeight: 600, color: "var(--text)" }}>{m.month}</td>
              {byPurchase && <td className="dd-num" style={tdR}>{(m.salesCount ?? 0).toLocaleString()}</td>}
              {byPurchase && <td className="dd-num" style={tdR}>{moneyFmt(m.salesValue ?? 0)}</td>}
              <td className="dd-num" style={tdR}>{m.count.toLocaleString()}</td>
              <td className="dd-num" style={tdR}>{moneyFmt(m.amount)}</td>
              {byPurchase && <td className="dd-num" style={tdR}>{m.refundRatePct !== null ? `${(m.refundRatePct * 100).toFixed(1)}%` : "—"}</td>}
              <td className="dd-num" style={tdR}>{moneyFmt(m.amountByBucket.sameDay)}</td>
              <td className="dd-num" style={tdR}>{moneyFmt(cum(m, w7))}</td>
              <td className="dd-num" style={tdR}>{moneyFmt(cum(m, w30))}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Refund detail BY PRODUCT (Praxis): which products drive refunds. The product
 *  portion of each refund is prorated across items by revenue share,
 *  with each product's own refund rate (refund $ ÷ that product's sales) and the
 *  cumulative refunded $ within same-day / ≤7-day / ≤30-day windows of the sale. */
function RefundProductTable({ rows }: { rows: RefundProductPoint[] }): React.ReactElement {
  const w7: BucketKey[] = ["sameDay", "d1", "d3", "d7"];
  const w30: BucketKey[] = ["sameDay", "d1", "d3", "d7", "d14", "d30"];
  const cum = (p: RefundProductPoint, keys: BucketKey[]): number => keys.reduce((s, k) => s + p.amountByBucket[k], 0);
  const data = rows.filter((p) => p.amount > 0); // already sorted by refund $ desc; drop products with no refunds
  const total = data.reduce((s, p) => s + p.amount, 0);
  const thR: React.CSSProperties = { textAlign: "right", padding: "6px 9px", fontWeight: 600, whiteSpace: "nowrap" };
  const tdR: React.CSSProperties = { textAlign: "right", padding: "6px 9px", whiteSpace: "nowrap" };
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
        <thead>
          <tr style={{ color: "var(--body-faint)", borderBottom: "1px solid var(--border)" }}>
            <th style={{ textAlign: "left", padding: "6px 9px", fontWeight: 600 }}>Product</th>
            <th style={thR}>Refunded $</th>
            <th style={thR}>Share</th>
            <th style={thR}>Refunded $ / sales $</th>
            <th style={thR}>Same-day $</th>
            <th style={thR}>≤7-day $</th>
            <th style={thR}>≤30-day $</th>
          </tr>
        </thead>
        <tbody>
          {data.map((p) => (
            <tr key={p.product} style={{ borderBottom: "1px solid var(--border-faint)" }}>
              <td style={{ textAlign: "left", padding: "6px 9px", fontWeight: 600, color: "var(--text)" }}>{p.product}</td>
              <td className="dd-num" style={tdR}>{moneyFmt(p.amount)}</td>
              <td className="dd-num" style={tdR}>{total > 0 ? `${((p.amount / total) * 100).toFixed(1)}%` : "—"}</td>
              <td className="dd-num" style={tdR}>{p.revenue > 0 ? `${((p.amount / p.revenue) * 100).toFixed(1)}%` : "—"}</td>
              <td className="dd-num" style={tdR}>{moneyFmt(p.amountByBucket.sameDay)}</td>
              <td className="dd-num" style={tdR}>{moneyFmt(cum(p, w7))}</td>
              <td className="dd-num" style={tdR}>{moneyFmt(cum(p, w30))}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: "1px solid var(--border)", fontWeight: 600, color: "var(--text)" }}>
            <td style={{ textAlign: "left", padding: "6px 9px" }}>All products</td>
            <td className="dd-num" style={tdR}>{moneyFmt(total)}</td>
            <td className="dd-num" style={tdR}>100%</td>
            <td className="dd-num" style={tdR}>—</td>
            <td className="dd-num" style={tdR}>{moneyFmt(data.reduce((s, p) => s + p.amountByBucket.sameDay, 0))}</td>
            <td className="dd-num" style={tdR}>{moneyFmt(data.reduce((s, p) => s + cum(p, w7), 0))}</td>
            <td className="dd-num" style={tdR}>{moneyFmt(data.reduce((s, p) => s + cum(p, w30), 0))}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// ===========================================================================
// Consolidated refund cards (founder 2026-07-13): the 9-card section → 3.
// Each old view remains reachable inside these three (nothing lost).
// ===========================================================================

/** Card 1 — headline tile: refund RATE big, with refunded $, % of revenue and
 *  avg refund as small sub-stats on the same card (absorbs those 3 old tiles). */
function RefundHeadlineCard({ rs, loading, rateSeries, rateDelta, avgRefund, refundPctRev, periodLabel, onClick }: {
  rs: Retention["summary"] | undefined;
  loading: boolean;
  rateSeries: number[];
  rateDelta: number | null;
  avgRefund: number | null;
  refundPctRev: number | null;
  periodLabel: string;
  onClick: () => void;
}): React.ReactElement {
  const stat = (label: string, value: string): React.ReactElement => (
    <div style={{ minWidth: 84 }}>
      <div style={{ fontSize: 11, color: "var(--body-faint)" }}>{label}</div>
      <div className="dd-num" style={{ fontSize: 15.5, fontWeight: 700, color: "var(--num)", marginTop: 2 }}>{value}</div>
    </div>
  );
  return (
    <Card onClick={onClick} className="dd-clickable-card" title="Click to explore">
      <CardHead title="Refund rate" explain={[
        "<b>What it is:</b> How often your orders come back as refunds, with the money impact right beside it — dollars refunded, what share of revenue that is, and the average refund size.",
        "<b>How it's calculated:</b> Refund rate = refunded orders divided by all orders in the window. Refunded $ is the actual cash returned on completed refunds. % of revenue = that cash returned divided by sales before refunds and after sales tax is removed. Average per refunded order = cash returned divided by refunded orders.",
        "<b>Example (sample data · year-to-date):</b> 681 refunded ÷ 15,018 orders = 4.5%; $40,362.76 refunded; 3.5% of revenue; $59.27 average.",
        "<b>Note:</b> Refund cash can include returned tax or shipping, while dashboard revenue removes sales tax. That makes % of revenue a cash-impact read, not a product-only refund rate. A product filter keeps orders containing that product and uses the whole order on both sides. Recent windows can rise as late refunds arrive.",
      ]} />
      {loading ? (
        <div className="dd-skeleton" style={{ height: 36, width: 130, marginTop: 10, borderRadius: 6 }} />
      ) : (
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", justifyContent: "space-between", gap: 20, marginTop: 8 }}>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 13 }}>
            <div className="dd-num" style={{ fontFamily: "var(--font-display)", fontSize: 34, fontWeight: 700, color: "var(--num)", lineHeight: 1 }}>{pct(rs?.refundRatePct)}</div>
            <div>
              {rateDelta !== null && (
                <span className="dd-num" style={{ fontSize: 12.5, fontWeight: 700, color: rateDelta <= 0 ? "var(--good)" : "var(--bad)" }}>
                  {rateDelta <= 0 ? "▼" : "▲"} {Math.abs(rateDelta * 100).toFixed(1)}%
                </span>
              )}
              <div style={{ fontSize: 11, color: "var(--body-faint)", marginTop: 2 }}>of orders · {periodLabel.toLowerCase()}</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 22, flexWrap: "wrap" }}>
            {stat("Refunded $", moneyFmt(rs?.refundedAmount ?? 0))}
            {stat("% of revenue", pct(refundPctRev))}
            {stat("Avg per refunded order", avgRefund === null ? "—" : moneyFmt(avgRefund))}
          </div>
        </div>
      )}
      {!loading && rateSeries.length > 2 && <div style={{ marginTop: 10 }}><Sparkline data={rateSeries} width={240} height={30} /></div>}
    </Card>
  );
}

/** Card 2 — True refund rate by month sold (COHORT). Monthly cohort bars (share
 *  of that month's orders eventually refunded), recent months faded as "still
 *  settling", with a per-product breakdown (absorbs Refund by product). */
function RefundCohortCard({ data, loading }: { data: RefundCohorts | undefined; loading: boolean }): React.ReactElement {
  const [basis, setBasis] = useState<"count" | "value">("count");
  const explain = [
    "<b>What it is:</b> Of the orders you took in each month, the share that eventually came back for a refund, no matter when the refund happened. The honest per-month return rate; the Refund rate card above is the whole-window view.",
    "<b>How it's calculated:</b> For each month, Orders = refunded orders divided by that month's orders. Dollars = actual refund cash divided by that month's sales before refunds and after sales tax is removed. Every later refund stays attached to the month of the original sale. Recent months are faded while refunds are still arriving.",
    "<b>Example:</b> If March took 2,700 orders and 119 of them were ever refunded, March's cohort rate is 4.4%, and it stays attached to March even when the refund lands in May.",
    "<b>Note:</b> Refund cash can include returned tax or shipping, so Dollars is a cash-impact ratio rather than a product-only refund rate. Pick a product and the card keeps orders containing it, using whole-order dollars on both sides. Faded months are not final yet.",
  ];
  const body = (): React.ReactElement => {
    if (loading) return <div className="dd-skeleton" style={{ height: 172, borderRadius: 8, marginTop: 12 }} />;
    if (!data || data.cohorts.length === 0) return <div style={{ color: "var(--body-faint)", fontSize: 12.5, marginTop: 14 }}>No orders in this window yet.</div>;
    const lagNote = data.lagBasis === "measured"
      ? `~${data.settleDays} days, your typical order→refund gap`
      : `~${data.settleDays} days, a typical refund window`;
    const months = data.cohorts.slice(-24);
    return (
      <div style={{ marginTop: 10 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14, fontSize: 10.5, color: "var(--body-faint)" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><i style={{ width: 10, height: 10, borderRadius: 2, background: "var(--accent)", display: "inline-block" }} />settled</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><i style={{ width: 10, height: 10, borderRadius: 2, display: "inline-block", background: "repeating-linear-gradient(45deg, var(--border), var(--border) 2px, transparent 2px, transparent 4px)", border: "1px solid var(--border)" }} />still settling</span>
          </div>
          <Segmented ariaLabel="Basis" size="sm" value={basis} onChange={(v) => setBasis(v)}
            options={[{ value: "count", label: "Orders" }, { value: "value", label: "Dollars" }]} />
        </div>
        <CohortBars months={months} basis={basis} />
        <div style={{ fontSize: 11, color: "var(--body-faint)", marginTop: 10, lineHeight: 1.5 }}>Faded months are still settling, so their refunds are still landing ({lagNote}) and their rate isn't final. Pick a product in the sidebar to see just that product's monthly rate.</div>
      </div>
    );
  };
  return (
    <Card>
      <CardHead title="True refund rate by month sold" explain={explain} />
      {body()}
    </Card>
  );
}

/** Vertical monthly cohort bars with a maturity fade — settling months hatched. */
function CohortBars({ months, basis, onPick }: {
  months: RefundCohortPoint[];
  basis: "count" | "value";
  onPick?: (month: string) => void;
}): React.ReactElement {
  const rateOf = (m: RefundCohortPoint): number => (basis === "count" ? m.rateCount : m.rateAmount) ?? 0;
  const max = Math.max(0.0001, ...months.map(rateOf));
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 168, marginTop: 12, overflowX: "auto", paddingBottom: 2 }}>
      {months.map((m) => {
        const v = rateOf(m);
        const h = Math.max(3, Math.round((v / max) * 132));
        const detail = basis === "count"
          ? `${m.refundedOrders.toLocaleString()}/${m.orders.toLocaleString()} orders`
          : `${moneyFmt(m.refundedAmount)} / ${moneyFmt(m.revenue)}`;
        return (
          <div key={m.month} onClick={onPick ? () => onPick(m.month) : undefined} title={`${m.month}: ${(v * 100).toFixed(1)}%${m.settling ? " · still settling" : ""} — ${detail}`}
            style={{ flex: "1 0 30px", minWidth: 30, display: "flex", flexDirection: "column", alignItems: "center", cursor: onPick ? "pointer" : "default" }}>
            <div className="dd-num" style={{ fontSize: 9.5, color: m.settling ? "var(--body-faint)" : "var(--num)", marginBottom: 3 }}>{(v * 100).toFixed(1)}</div>
            <div style={{ width: "74%", height: h, borderRadius: "4px 4px 0 0",
              background: m.settling ? "repeating-linear-gradient(45deg, var(--border), var(--border) 3px, transparent 3px, transparent 6px)" : "var(--accent)",
              border: m.settling ? "1px solid var(--border)" : "none" }} />
            <div style={{ fontSize: 9.5, color: "var(--body-faint)", marginTop: 5, whiteSpace: "nowrap" }}>{m.month.slice(2)}</div>
          </div>
        );
      })}
    </div>
  );
}


/** Card 3 — Refund money over time (FLOW, by refund date). Absorbs the $/rate
 *  combo + Refunds by refund date; the detail tables (incl. speed buckets) open
 *  from "Show detail". */
function RefundFlowCard({ ra, loading }: { ra: RefundAnalysis | undefined; loading: boolean }): React.ReactElement {
  const [showDetail, setShowDetail] = useState(false);
  const explain = [
    "<b>What it is:</b> The refund money leaving each month — the cash-out view, arranged by when the refund actually happened. It pairs with the rate card above (arranged by when the sale happened).",
    "<b>How it's calculated:</b> Completed refunds summed by the month of the refund (at refund-time exchange rates); the line is how many refund transactions landed that month. One order can have more than one partial refund. ‘Show detail’ opens the speed breakdown (how fast refunds came back) and the by-sale-date / by-product tables.",
    "<b>Example:</b> A month's bar mixes refunds on that month's own sales with refunds on sales from earlier months.",
    "<b>Note:</b> Follows the sidebar filters. This is money by REFUND date, so it won't line up with the by-sale-date rate card month for month. If your provider hasn't sent true refund dates yet, this waits for them rather than guessing from the import date.",
  ];
  const detailBtn: React.CSSProperties = { border: "1px solid var(--border)", background: "var(--surface-2)", color: "var(--body)", borderRadius: 7, padding: "4px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" };
  const subHead: React.CSSProperties = { fontSize: 12.5, fontWeight: 700, color: "var(--text)", marginBottom: 8 };
  if (loading) {
    return <Card><CardHead title="Refund money over time" explain={explain} /><div className="dd-skeleton" style={{ height: 200, borderRadius: 8, marginTop: 12 }} /></Card>;
  }
  if (!ra || (ra.byRefund.length === 0 && ra.byPurchase.length === 0)) {
    return <Card><CardHead title="Refund money over time" explain={explain} /><div style={{ color: "var(--body-faint)", fontSize: 12.5, marginTop: 14 }}>No refunds in this window yet.</div></Card>;
  }
  const importing = ra.refundDatesImporting;
  const missingSaleBasis = ra.missingSaleBasis ?? { orders: 0, amount: 0 };
  const segLabels = ra.buckets.map((b) => b.label);
  const toStacked = (rows: RefundMonthPoint[]): StackedPoint[] =>
    rows.map((m) => ({ label: m.month, segments: ra.buckets.map((b) => m.amountByBucket[b.key]), line: m.count }));
  return (
    <Card>
      <CardHead title="Refund money over time" explain={explain}>
        <button onClick={() => setShowDetail((s) => !s)} style={detailBtn}>{showDetail ? "Hide detail" : "Show detail"}</button>
      </CardHead>
      {importing ? <RefundTimingImporting what="The money-out-by-refund-month view" /> : (
        <div style={{ marginTop: 12 }}>
          <ComboChart barLabel="Refunded $" lineLabel="Refund events" points={ra.byRefund.map((m) => ({ label: m.month, bar: m.amount, line: m.count }))} />
        </div>
      )}
      {missingSaleBasis.orders > 0 && (
        <div style={{ marginTop: 12, padding: "10px 12px", borderRadius: 8, background: "color-mix(in srgb, var(--warn, #b5820a) 9%, var(--surface))", border: "1px solid color-mix(in srgb, var(--warn, #b5820a) 45%, var(--border))", color: "var(--text)", fontSize: 12, lineHeight: 1.5 }}>
          <strong>Refund history needs a quick check.</strong> {missingSaleBasis.orders.toLocaleString()} refunded {missingSaleBasis.orders === 1 ? "order has" : "orders have"} no original sales amount. The cash-out chart includes the {moneyFmt(missingSaleBasis.amount)} returned. Refund rates and product tables leave {missingSaleBasis.orders === 1 ? "that order" : "those orders"} out because there is no honest sales number to compare against. Open Show detail for the provider and order IDs so they can be matched at the source.
        </div>
      )}
      {showDetail && (
        <div style={{ marginTop: 18, display: "grid", gap: 18 }}>
          <RefundTransactions />
          <div>
            <div style={subHead}>How fast refunds came back (by refund month)</div>
            {importing ? <RefundTimingImporting what="The speed split" /> : <StackedBarLineChart points={toStacked(ra.byRefund)} segLabels={segLabels} lineLabel="Refund events" />}
          </div>
          <div>
            <div style={subHead}>Refund detail — by refund date</div>
            {importing ? <RefundTimingImporting what="The by-refund-date table" /> : <RefundTable rows={ra.byRefund} byPurchase={false} />}
          </div>
          <div>
            <div style={subHead}>Refund detail — by sale date</div>
            {importing ? <RefundTimingImporting what="The by-sale-date speed columns" /> : <RefundTable rows={ra.byPurchase} byPurchase />}
          </div>
          <div>
            <div style={subHead}>Refund detail — by product</div>
            <div style={{ fontSize: 11, color: "var(--body-faint)", marginBottom: 8, lineHeight: 1.45 }}>Your store gives us one refund total for the whole order. We estimate the product part based on the product sales share of the original charge, remove shipping and tax, then split it across the products bought. This keeps refunded product dollars and product sales on the same basis.</div>
            <RefundProductTable rows={ra.byProduct} />
          </div>
        </div>
      )}
    </Card>
  );
}

function csvCell(value: string | number): string {
  const text = String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/** Loaded only after Show detail is opened, so the export never slows the page. */
function RefundTransactions(): React.ReactElement {
  const api = useApiClient();
  const period = usePeriod();
  const eventsQ = useQuery({
    queryKey: ["ops-refund-events", period.cacheKey],
    queryFn: () => api.get<{ events: RefundEvent[] }>("/dashboard/refund-events", { ...period.query }),
  });
  const events = eventsQ.data?.events ?? [];
  const rows = events.slice(0, 100);
  const exportCsv = (): void => {
    const header = ["Provider", "Order ID", "Sale date", "Refund date", "Days to refund", "Refund amount", "Original sale amount", "Needs source match", "Reason"];
    const lines = [header, ...events.map((event) => [
      event.provider,
      event.orderId,
      event.saleDate.slice(0, 10),
      event.refundDate.slice(0, 10),
      event.daysAfterSale,
      event.amount.toFixed(2),
      event.originalSaleAmount.toFixed(2),
      event.missingSaleBasis ? "Yes" : "No",
      event.reason ?? "",
    ])].map((line) => line.map(csvCell).join(","));
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `refund-transactions-${period.cacheKey.replace(/[^a-zA-Z0-9_-]/g, "-")}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };
  const button: React.CSSProperties = { border: "1px solid var(--border)", background: "var(--surface-2)", color: "var(--text)", borderRadius: 7, padding: "5px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer" };
  const head: React.CSSProperties = { padding: "7px 8px", textAlign: "left", fontSize: 10.5, color: "var(--body-faint)", borderBottom: "1px solid var(--border)", whiteSpace: "nowrap" };
  const cell: React.CSSProperties = { padding: "7px 8px", fontSize: 11.5, color: "var(--body)", borderBottom: "1px solid var(--border)", whiteSpace: "nowrap" };
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 8 }}>
        <div>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--text)" }}>Refund transactions</div>
          <div style={{ fontSize: 11, color: "var(--body-faint)", marginTop: 2 }}>Use the provider and order ID to match any missing sale at the source.</div>
        </div>
        <button type="button" onClick={exportCsv} disabled={events.length === 0} style={{ ...button, opacity: events.length > 0 ? 1 : 0.5 }}>Export CSV</button>
      </div>
      {eventsQ.isLoading ? <div style={{ fontSize: 12, color: "var(--body-faint)" }}>Loading refund transactions…</div> : eventsQ.isError ? (
        <div role="alert" style={{ fontSize: 12, color: "var(--bad)" }}>We couldn't load the refund transactions. Try again.</div>
      ) : events.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--body-faint)" }}>No refund transactions in this window.</div>
      ) : (
        <>
          <div style={{ overflowX: "auto", maxHeight: 340, overflowY: "auto", border: "1px solid var(--border)", borderRadius: 8 }}>
            <table style={{ borderCollapse: "collapse", width: "100%" }}>
              <thead><tr><th style={head}>Provider</th><th style={head}>Order ID</th><th style={head}>Sale date</th><th style={head}>Refund date</th><th style={{ ...head, textAlign: "right" }}>Refund</th><th style={head}>Check</th></tr></thead>
              <tbody>{rows.map((event) => (
                <tr key={event.id}>
                  <td style={{ ...cell, textTransform: "capitalize" }}>{event.provider}</td>
                  <td style={{ ...cell, color: "var(--text)", fontWeight: 600 }}>{event.orderId}</td>
                  <td style={cell}>{event.saleDate.slice(0, 10)}</td>
                  <td style={cell}>{event.refundDate.slice(0, 10)}{event.refundDateConfirmed ? "" : "*"}</td>
                  <td className="dd-num" style={{ ...cell, textAlign: "right", color: "var(--num)" }}>{moneyFmt(event.amount)}</td>
                  <td style={{ ...cell, color: event.missingSaleBasis ? "var(--bad)" : "var(--good)", fontWeight: 600 }}>{event.missingSaleBasis ? "Match sale" : "Matched"}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
          {events.some((event) => !event.refundDateConfirmed) && <div style={{ fontSize: 10.5, color: "var(--body-faint)", marginTop: 5 }}>* The provider did not send the true refund date, so this shows the import date.</div>}
          {events.length > rows.length && <div style={{ fontSize: 10.5, color: "var(--body-faint)", marginTop: 5 }}>Showing the first {rows.length.toLocaleString()} of {events.length.toLocaleString()}. The CSV includes all of them.</div>}
        </>
      )}
    </div>
  );
}
